import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Icon from '@mui/material/Icon';
import 'react-notifications-component/dist/theme.css';

import rtlPlugin from 'stylis-plugin-rtl';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import { useIntl } from 'react-intl';
import { Alert, AlertTitle } from '@mui/material';
import { Helmet } from 'react-helmet-async';
import MDBox from 'components/MDBox';
import { setOpenConfigurator, useMaterialUIController } from 'context';
import useRoutes from 'hook/useRoutes';
import useConf from 'hook/useConf';
import { useGetConfigurationQuery } from 'services/applicationApi';
import IndeterminateLoading from 'components/Loading/IndeterminateLoading';
import { fErrors } from 'utils/formatError';
import { NOT_FOUND } from 'utils/messageUtils';
import { MainLayout } from 'components/LayoutContainers/MainLayout';

export default function App() {
  const { formatMessage: msg } = useIntl();
  const { pathname } = useLocation();

  const {
    getValue: conf,
    isLoading: confLoading,
    error: confError
  } = useConf(useGetConfigurationQuery);

  const { controller, dispatch } = useMaterialUIController();
  const { direction, layout, openConfigurator } = controller;

  const [rtlCache, setRtlCache] = useState(null);

  const routes = useRoutes();

  // Cache for the rtl
  useMemo(() => {
    const cacheRtl = createCache({
      key: 'rtl',
      stylisPlugins: [rtlPlugin]
    });

    setRtlCache(cacheRtl);
  }, []);

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute('dir', direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const configsButton = useMemo(() => {
    if (conf('ui.customizable')) {
      return (
        <MDBox
          display="flex"
          justifyContent="center"
          alignItems="center"
          width="3.25rem"
          height="3.25rem"
          bgColor="white"
          shadow="sm"
          borderRadius="50%"
          position="fixed"
          right="2rem"
          bottom="2rem"
          zIndex={99}
          color="dark"
          sx={{ cursor: 'pointer' }}
          onClick={() => setOpenConfigurator(dispatch, !openConfigurator)}
        >
          <Icon fontSize="small" color="inherit">
            settings
          </Icon>
        </MDBox>
      );
    }
    return null;
  }, [conf, dispatch, openConfigurator]);

  if (confLoading) {
    return (
      <IndeterminateLoading
        sx={{ width: '100%', color: '#125688' }}
        message={msg({ id: 'loading.configuration' })}
      />
    );
  }
  if (confError) {
    return (
      <Alert severity="error">
        <AlertTitle>{msg({ id: 'notification.error.title' })}</AlertTitle>
        {msg({ id: 'error.configuration' }, { error: fErrors(msg, confError) })}
      </Alert>
    );
  }
  return (
    <>
      <Helmet>
        <meta name="description" content={msg({ id: 'product.description' })} />
      </Helmet>
      {direction === 'rtl' ? (
        <CacheProvider value={rtlCache}>
          <MainLayout
            layout={layout}
            brandName={msg({ id: 'product.logo.name', defaultMessage: NOT_FOUND })}
            routes={routes}
            configsButton={configsButton}
          />
        </CacheProvider>
      ) : (
        <MainLayout
          layout={layout}
          brandName={msg({ id: 'product.logo.name', defaultMessage: NOT_FOUND })}
          routes={routes}
          configsButton={configsButton}
        />
      )}
    </>
  );
}
